import {
  ApiRoute,
  CreateEditRevisionDto,
  CreateProgram,
  PageData,
  PageDataSchema,
  ProgramQuestionDto,
  ProgramQuestionDtoSchema,
  ProgramRevision,
  ProgramRevisionSchema,
  ProgramWithDetail,
  ProgramWithDetailSchema,
  QueryProgramsReq,
  SubmitForReviewDto,
  UpdateProgram,
  UpsertProgramQuestions,
  getApiRoute
} from "@humanex/interfaces";
import { z } from "zod";

import { ApiClientConfig, BaseApiClient } from "./base-client";

const MessageSchema = z.object({ message: z.string() });

export class ProgramApiClient extends BaseApiClient {
  constructor(config: ApiClientConfig) {
    super({
      ...config,
      baseEndpoint: getApiRoute(ApiRoute.PROGRAM),
      requireAuth: false
    });
  }

  // ---------------------------------------------------------------------------
  // Reads
  // ---------------------------------------------------------------------------

  async getList(params?: Partial<QueryProgramsReq>): Promise<PageData<ProgramWithDetail>> {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.type) query.set("type", params.type);
    if (params?.orgId) query.set("orgId", params.orgId);
    if (params?.page != null) query.set("page", String(params.page));
    if (params?.limit != null) query.set("limit", String(params.limit));
    if (params?.featured !== undefined) query.set("featured", String(params.featured));
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    return this.get(qs ? `?${qs}` : "", PageDataSchema(ProgramWithDetailSchema), { requireAuth: false });
  }

  async getOne(id: string): Promise<ProgramWithDetail> {
    return this.get(id, ProgramWithDetailSchema, { requireAuth: false });
  }

  async getRevisions(id: string): Promise<ProgramRevision[]> {
    return this.get(`/${id}/revisions`, z.array(ProgramRevisionSchema), { requireAuth: true });
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  async create(data: CreateProgram): Promise<ProgramWithDetail> {
    return this.post("", ProgramWithDetailSchema, data, { requireAuth: true });
  }

  async update(id: string, data: UpdateProgram): Promise<ProgramWithDetail> {
    return this.put(`/${id}`, ProgramWithDetailSchema, data, { requireAuth: true });
  }

  async deleteProgram(id: string): Promise<void> {
    await this.delete(`/${id}`, MessageSchema, { requireAuth: true });
  }

  // ---------------------------------------------------------------------------
  // State-machine transitions
  // ---------------------------------------------------------------------------

  async submitForReview(id: string, data: SubmitForReviewDto): Promise<void> {
    await this.post(`/${id}/submit`, MessageSchema, data, { requireAuth: true });
  }

  async publishProgram(id: string): Promise<void> {
    await this.post(`/${id}/publish`, MessageSchema, {}, { requireAuth: true });
  }

  async createEditRevision(id: string, data: CreateEditRevisionDto): Promise<void> {
    await this.post(`/${id}/edit`, MessageSchema, data, { requireAuth: true });
  }

  async pauseProgram(id: string): Promise<void> {
    await this.patch(`/${id}/pause`, MessageSchema, {}, { requireAuth: true });
  }

  async resumeProgram(id: string): Promise<void> {
    await this.patch(`/${id}/resume`, MessageSchema, {}, { requireAuth: true });
  }

  async closeProgram(id: string): Promise<void> {
    await this.patch(`/${id}/close`, MessageSchema, {}, { requireAuth: true });
  }

  // ---------------------------------------------------------------------------
  // Questions
  // ---------------------------------------------------------------------------

  async upsertQuestions(id: string, data: UpsertProgramQuestions): Promise<ProgramQuestionDto[]> {
    return this.post(`/${id}/questions`, z.array(ProgramQuestionDtoSchema), data, { requireAuth: true });
  }
}
